import { useEffect, useRef, useState } from 'react';

/**
 * Piezas de **fondo** de una slide del carrusel, compartidas por el carrusel de
 * bienvenida (`OnboardingCarousel`) y el hero del paywall (`PricingHero`).
 *
 * Se extrajeron acá tal cual estaban en `OnboardingCarousel` (ADR-2026-07-28-rediseno-paywall-catalogo-componentes-presentacionales): son la
 * misma máquina de carga progresiva (imagen y video), y duplicarlas para el
 * paywall habría dejado dos copias que se desincronizan. No hay cambios de
 * comportamiento respecto de la versión original.
 *
 * **Sólo lo cercano baja en alta** (ADR-2026-10-18-los-planes-cargan-solo-lo-que-se-ve). Los dos
 * carruseles montan TODAS las slides a la vez, una encima de la otra. Así, abrir los planes
 * bajaba la foto y los dos videos de las cuatro: ~9 MB en moda, antes de que la persona viera un
 * precio. Con `cerca={false}` la slide muestra sólo su foto liviana (`preview`). La original y
 * los videos se piden recién cuando pasa a estar cerca (la que se ve o la siguiente), y una vez
 * pedidos se quedan. `cerca` es opcional y vale `true`, que es como funcionaba antes.
 */

/** `true` desde la primera vez que la slide estuvo cerca: lo que ya se pidió no se suelta. */
function useYaEstuvoCerca(cerca: boolean): boolean {
  const [ya, setYa] = useState(cerca);
  useEffect(() => {
    if (cerca) setYa(true);
  }, [cerca]);
  return ya || cerca;
}

/**
 * Fondo con **carga progresiva** (ADR-742): muestra al instante la versión
 * `preview` (optimizada, ~500px) y encima la `src` (resolución original), que
 * arranca invisible y aparece con un fade corto **recién cuando termina de cargar**.
 * Mientras la original no cargó, el usuario ve la optimizada; el reemplazo es
 * imperceptible (misma imagen, mismo encuadre, sólo más nítida).
 *
 * Por defecto ambas se anclan **arriba** (`top`): se recorta el pie, que en mobile queda
 * tapado por el scrim del contenido. Con `objectPosition` una slide puede pedir otro
 * encuadre (p.ej. `center`); ver `imagePosition` en `onboardingSlides` (ADR-798).
 */
export function ProgressiveImage({
  preview,
  src,
  alt,
  objectPosition = 'top',
  cerca = true,
}: {
  preview: string;
  src: string;
  alt: string;
  objectPosition?: string;
  /** La slide se ve o es la siguiente: recién ahí se pide la original. */
  cerca?: boolean;
}) {
  const [loaded, setLoaded] = useState(false);
  const fullRef = useRef<HTMLImageElement>(null);
  const cargar = useYaEstuvoCerca(cerca);

  // Si la original ya está en caché, el `onLoad` puede no dispararse (la imagen ya
  // está `complete` al montar): lo chequeamos para revelarla igual. Depende de `cargar`
  // porque la original se monta recién cuando la slide está cerca.
  useEffect(() => {
    const img = fullRef.current;
    if (img?.complete && img.naturalWidth > 0) setLoaded(true);
  }, [cargar]);

  return (
    <>
      {/* Optimizada: base, siempre visible. */}
      <img
        src={preview}
        alt=""
        aria-hidden
        draggable={false}
        className="absolute inset-0 size-full object-cover"
        style={{ objectPosition }}
      />
      {/* Original: encima; fade imperceptible al terminar de cargar. */}
      {cargar && (
        <img
          ref={fullRef}
          src={src}
          alt={alt}
          draggable={false}
          onLoad={() => setLoaded(true)}
          className="absolute inset-0 size-full object-cover transition-opacity duration-500 ease-out"
          style={{ opacity: loaded ? 1 : 0, objectPosition }}
        />
      )}
    </>
  );
}

/**
 * Fondo en **video** de una slide (ADR-758; progresivo + reinicio en ADR-761). Se monta
 * ENCIMA del `ProgressiveImage` (póster / instant-paint / fallback con reduced-motion).
 *
 * **Progresivo** (mismo patrón que las imágenes): abajo va la versión **optimizada**
 * (`preview`, liviana, arranca rápido) y encima el **original** (`src`, full), que empieza
 * invisible y hace un fade cuando terminó de bufferear (`canplaythrough`), sincronizado al
 * tiempo del optimizado para que el swap sea imperceptible.
 *
 * **Sólo reproduce la slide activa:** al activarse, ambos videos se **reinician a 0** y se
 * reproducen (arranca de nuevo cada vez que el carrusel vuelve a esta slide); al salir se
 * **pausan** (no queda un video corriendo por detrás mientras se ve otra slide).
 *
 * `muted` + `playsInline` habilitan el autoplay sin gesto (iOS/Safari); `aria-hidden` porque
 * el texto accesible vive en el póster.
 */
export function SlideVideo({
  preview,
  src,
  poster,
  active,
  objectPosition = 'top',
  cerca = true,
}: {
  preview: string;
  src: string;
  poster: string;
  active: boolean;
  /** Anclaje del encuadre (`object-position`). Default `top`, como las imágenes. ADR-795. */
  objectPosition?: string;
  /** La slide se ve o es la siguiente: recién ahí se montan los videos (y su póster). */
  cerca?: boolean;
}) {
  const cargar = useYaEstuvoCerca(cerca);
  if (!cargar) return null;
  return <VideosDeLaSlide preview={preview} src={src} poster={poster} active={active} objectPosition={objectPosition} />;
}

/** Los dos `<video>` de la slide; aparte para que `SlideVideo` pueda no montarlos sin romper los hooks. */
function VideosDeLaSlide({
  preview,
  src,
  poster,
  active,
  objectPosition,
}: {
  preview: string;
  src: string;
  poster: string;
  active: boolean;
  objectPosition: string;
}) {
  const optRef = useRef<HTMLVideoElement>(null);
  const fullRef = useRef<HTMLVideoElement>(null);
  const [fullReady, setFullReady] = useState(false);

  const restart = (v: HTMLVideoElement | null) => {
    if (!v) return;
    try {
      v.currentTime = 0;
    } catch {
      /* aún sin metadata: arrancará en 0 igual */
    }
    const p = v.play();
    if (p && typeof p.catch === 'function') p.catch(() => {});
  };

  // Activación → reinicia (0) y reproduce; desactivación → pausa ambos. Sólo depende de
  // `active` (no de `fullReady`) para no reiniciar el optimizado cuando carga el original.
  useEffect(() => {
    const opt = optRef.current;
    const full = fullRef.current;
    if (active) {
      restart(opt);
      if (full && full.readyState >= 3) restart(full); // HAVE_FUTURE_DATA
    } else {
      opt?.pause();
      full?.pause();
    }
  }, [active]);

  return (
    <>
      {/* Optimizado: base, arranca rápido. */}
      <video
        ref={optRef}
        src={preview}
        poster={poster}
        muted
        loop
        playsInline
        preload="auto"
        aria-hidden
        className="absolute inset-0 size-full object-cover"
        style={{ objectPosition }}
      />
      {/* Original (full): encima; fade-in imperceptible al bufferear, sincronizado. */}
      <video
        ref={fullRef}
        src={src}
        muted
        loop
        playsInline
        preload="auto"
        aria-hidden
        onCanPlayThrough={() => {
          setFullReady(true);
          const full = fullRef.current;
          if (active && full) {
            // Sincroniza con el optimizado para que el cambio no dé un salto.
            try {
              full.currentTime = optRef.current?.currentTime ?? 0;
            } catch {
              /* noop */
            }
            const p = full.play();
            if (p && typeof p.catch === 'function') p.catch(() => {});
          }
        }}
        className="absolute inset-0 size-full object-cover transition-opacity duration-500 ease-out"
        style={{ opacity: fullReady && active ? 1 : 0, objectPosition }}
      />
    </>
  );
}
